import React, { useState } from 'react';
import { Book, X, Plus, Star, Clock, Lightbulb, User, Sparkles, ExternalLink } from 'lucide-react';
import { useReadingContext } from '../../../contexts/ReadingContext';

export default function BookSwitcherModal({ isOpen, onClose, currentBook }) {
  const { 
    readingList, 
    familyRecommendations,
    startNewBook, 
    addToReadingList,
    handleRecommendationAction,
    showToast 
  } = useReadingContext();
  
  const [activeTab, setActiveTab] = useState('reading-list'); // 'reading-list', 'add-book', or 'book-recommendations'
  const [newBookForm, setNewBookForm] = useState({
    title: '',
    author: '',
    totalPages: '',
    description: '',
    genre: ''
  });

  const handleAddNewBook = async () => {
    if (!newBookForm.title.trim() || !newBookForm.author.trim()) {
      showToast('Please enter at least a title and author', 'error');
      return;
    }
    
    try {
      const bookData = {
        title: newBookForm.title,
        author: newBookForm.author,
        totalPages: newBookForm.totalPages ? parseInt(newBookForm.totalPages) : null,
        description: newBookForm.description,
        genre: newBookForm.genre
      };
      
      await addToReadingList(bookData);
      showToast(`Added "${bookData.title}" to your reading list! 📚`);
      
      // Reset form
      setNewBookForm({
        title: '',
        author: '',
        totalPages: '',
        description: '',
        genre: ''
      });
      
      // Switch back to reading list tab
      setActiveTab('reading-list');
    } catch (error) {
      console.error('Failed to add book:', error);
      showToast('Failed to add book', 'error');
    }
  };

  const handleStartBook = async (bookData) => {
    try {
      await startNewBook(bookData);
      showToast(`Started reading "${bookData.title}"! 📖`);
      onClose();
    } catch (error) {
      console.error('Failed to start book:', error);
      showToast('Failed to start book', 'error');
    }
  };



  const handleStartRecommendation = async (recommendation) => {
    const bookData = {
      title: recommendation.book_title,
      author: recommendation.book_author,
      totalPages: null // Recommendations may not have page counts
    };
    
    try {
      await startNewBook(bookData);
      // Also update the recommendation status
      if (handleRecommendationAction) {
        await handleRecommendationAction(recommendation.id, 'reading', bookData);
      }
      showToast(`Started reading "${bookData.title}"! 📖`);
      onClose();
    } catch (error) {
      console.error('Failed to start recommended book:', error);
      showToast('Failed to start book', 'error');
    }
  };

  const handleAddRecommendationToList = async (recommendation) => {
    const bookData = {
      title: recommendation.book_title,
      author: recommendation.book_author,
      description: recommendation.recommendation_reason
    };
    
    try {
      await addToReadingList(bookData);
      // Update recommendation status
      if (handleRecommendationAction) {
        await handleRecommendationAction(recommendation.id, 'add_to_list', bookData);
      }
      showToast('Added to your reading list! 📚');
    } catch (error) {
      console.error('Failed to add recommendation to list:', error);
      showToast('Failed to add to reading list', 'error');
    }
  };

  const handleViewOnAmazon = (book) => {
    const title = book.book_title || book.title;
    const author = book.book_author || book.author;
    const searchQuery = encodeURIComponent(`${title} ${author}`);
    const amazonUrl = `https://www.amazon.com/s?k=${searchQuery}&i=stripbooks`;
    window.open(amazonUrl, '_blank');
  };

  const getBookInitials = (title) => {
    return title
      .split(' ')
      .slice(0, 2)
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase();
  };

  const getBookCover = (book) => {
    // Check for cover URL in different possible fields
    const coverUrl = book.cover_url || book.link_metadata?.image || book.image_url;
    
    if (coverUrl) {
      return (
        <img 
          src={coverUrl} 
          alt={book.book_title || book.title || 'Book cover'}
          className="w-full h-full object-cover rounded-lg"
          onError={(e) => {
            // Fallback to initials if image fails to load
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'flex';
          }}
        />
      );
    }
    
    return null;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-stone-200">
          <h2 className="text-xl font-bold text-slate-900">Switch Book</h2>
          <button
            onClick={onClose}
            className="p-2 text-stone-400 hover:text-slate-600 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Current Book Info */}
        {currentBook && (
          <div className="p-6 bg-amber-50 border-b border-amber-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-13 relative overflow-hidden rounded-lg">
                {getBookCover(currentBook)}
                <div className="w-full h-full bg-gradient-to-br from-amber-500 to-orange-500 rounded-lg flex items-center justify-center text-white text-sm font-bold absolute inset-0">
                  {getBookInitials(currentBook.book_title)}
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">{currentBook.book_title}</h3>
                <p className="text-sm text-amber-700">by {currentBook.book_author}</p>
                <p className="text-xs text-amber-600 mt-1">
                  Currently on page {currentBook.current_page}
                  {currentBook.total_pages && ` of ${currentBook.total_pages}`}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b border-stone-200">
          <button
            onClick={() => setActiveTab('reading-list')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'reading-list'
                ? 'text-azure-600 border-b-2 border-azure-600 bg-azure-50'
                : 'text-stone-500 hover:text-slate-700'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <Book className="w-4 h-4" />
              Reading List ({readingList?.length || 0})
            </div>
          </button>
          <button
            onClick={() => setActiveTab('book-recommendations')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'book-recommendations'
                ? 'text-violet-600 border-b-2 border-violet-600 bg-violet-50'
                : 'text-stone-500 hover:text-slate-700'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <Lightbulb className="w-4 h-4" />
              Recommendations ({familyRecommendations?.length || 0})
            </div>
          </button>
          <button
            onClick={() => setActiveTab('add-book')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'add-book'
                ? 'text-emerald-600 border-b-2 border-emerald-600 bg-emerald-50'
                : 'text-stone-500 hover:text-slate-700'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <Plus className="w-4 h-4" />
              Add New Book
            </div>
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-96">
          {activeTab === 'reading-list' && (
            <div className="p-6">
              {readingList && readingList.length > 0 ? (
                <div className="space-y-3">
                  {readingList.map((book) => (
                    <div key={book.id} className="flex items-center gap-3 p-4 border border-stone-200 rounded-xl hover:bg-stone-50 transition-colors">
                      <div className="w-10 h-13 relative overflow-hidden rounded-lg">
                        {getBookCover(book)}
                        <div className="w-full h-full bg-gradient-to-br from-azure-500 to-azure-600 rounded-lg flex items-center justify-center text-white text-sm font-bold absolute inset-0">
                          {getBookInitials(book.book_title)}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-slate-900 truncate">{book.book_title}</h3>
                        <p className="text-sm text-azure-600">by {book.book_author}</p>
                        {book.total_pages && (
                          <p className="text-xs text-stone-500">{book.total_pages} pages</p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleStartBook({
                            title: book.book_title,
                            author: book.book_author,
                            totalPages: book.total_pages
                          })}
                          className="px-4 py-2 bg-azure-500 text-white text-sm font-medium rounded-lg hover:bg-azure-600 transition-colors"
                        >
                          Start Reading
                        </button>
                        <button
                          onClick={() => handleViewOnAmazon(book)}
                          className="px-3 py-2 bg-orange-500 text-white text-sm font-medium rounded-lg hover:bg-orange-600 transition-colors flex items-center gap-1"
                          title="View on Amazon"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Book className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">No Books in List</h3>
                  <p className="text-slate-600 mb-4">Search for books to add to your reading list!</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'book-recommendations' && (
            <div className="p-6">
              {familyRecommendations && familyRecommendations.length > 0 ? (
                <div className="space-y-3">
                  {familyRecommendations.map((rec) => (
                    <div key={rec.id} className="border border-violet-200 rounded-xl p-4 hover:bg-violet-50 transition-colors">
                      <div className="flex gap-3 items-start">
                        {/* Book Cover */}
                        <div className="w-10 h-13 relative overflow-hidden rounded-lg flex-shrink-0">
                          {getBookCover(rec)}
                          <div className="w-full h-full bg-gradient-to-br from-violet-500 to-violet-600 rounded-lg flex items-center justify-center text-white text-sm font-bold absolute inset-0">
                            {getBookInitials(rec.book_title)}
                          </div>
                        </div>

                        {/* Book Info */}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-slate-900 text-sm mb-1 truncate">
                            {rec.book_title || 'Unknown Title'}
                          </h3>
                          <p className="text-xs text-violet-600 mb-2">by {rec.book_author || 'Unknown Author'}</p>
                          
                          {/* Source Badge */}
                          <div className="inline-flex items-center gap-1 px-2 py-1 bg-violet-100 rounded-full text-xs text-violet-700 font-medium mb-2">
                            {rec.source === 'family_member' ? (
                              <>
                                <User className="w-3 h-3" />
                                {rec.recommended_by_name} recommended
                              </>
                            ) : (
                              <>
                                <Sparkles className="w-3 h-3" />
                                Family library suggestion
                              </>
                            )}
                          </div>

                          {/* Recommendation Reason */}
                          {rec.recommendation_reason && (
                            <p className="text-xs text-violet-700 mb-3 line-clamp-2">
                              "{rec.recommendation_reason}"
                            </p>
                          )}

                          {/* Action Buttons */}
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleStartRecommendation(rec)}
                              className="px-3 py-1.5 bg-violet-500 text-white text-xs font-medium rounded-md hover:bg-violet-600 transition-colors"
                            >
                              Start Reading
                            </button>
                            <button
                              onClick={() => handleAddRecommendationToList(rec)}
                              className="px-3 py-1.5 bg-white border border-violet-300 text-violet-600 text-xs font-medium rounded-md hover:bg-violet-50 transition-colors"
                            >
                              Add to List
                            </button>
                            <button
                              onClick={() => handleViewOnAmazon(rec)}
                              className="px-3 py-1.5 bg-orange-500 text-white text-xs font-medium rounded-md hover:bg-orange-600 transition-colors flex items-center gap-1"
                              title="View on Amazon"
                            >
                              <ExternalLink className="w-3 h-3" />
                              Amazon
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Lightbulb className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">No Recommendations</h3>
                  <p className="text-slate-600 mb-4">Ask your family for book recommendations!</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'add-book' && (
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Book Title *
                  </label>
                  <input
                    type="text"
                    value={newBookForm.title}
                    onChange={(e) => setNewBookForm({...newBookForm, title: e.target.value})}
                    placeholder="Enter the book title"
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Author *
                  </label>
                  <input
                    type="text"
                    value={newBookForm.author}
                    onChange={(e) => setNewBookForm({...newBookForm, author: e.target.value})}
                    placeholder="Enter the author name"
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Pages (optional)
                    </label>
                    <input
                      type="number"
                      value={newBookForm.totalPages}
                      onChange={(e) => setNewBookForm({...newBookForm, totalPages: e.target.value})}
                      placeholder="300"
                      className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Genre (optional)
                    </label>
                    <input
                      type="text"
                      value={newBookForm.genre}
                      onChange={(e) => setNewBookForm({...newBookForm, genre: e.target.value})}
                      placeholder="Fiction, Mystery, etc."
                      className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Description (optional)
                  </label>
                  <textarea
                    value={newBookForm.description}
                    onChange={(e) => setNewBookForm({...newBookForm, description: e.target.value})}
                    placeholder="What's this book about?"
                    rows="3"
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                </div>
                
                <button
                  onClick={handleAddNewBook}
                  disabled={!newBookForm.title.trim() || !newBookForm.author.trim()}
                  className="w-full px-4 py-3 bg-emerald-500 text-white font-medium rounded-lg hover:bg-emerald-600 disabled:bg-stone-300 disabled:cursor-not-allowed transition-colors"
                >
                  Add Book to My Reading List
                </button>
                
                <p className="text-xs text-stone-500 text-center">
                  * Required fields. After adding, you can start reading from your reading list.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}